import {
  Button,
  Icon,
  Tabs,
  LoadingPost,
  PullToRefresh,
  MaleCard,
  Modal,
} from 'components';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import SALON_INQUIRY_BANNER_URL from '../../assets/images/teco_salon_banner0813.webp';
import {
  queryKeys,
  TEXT_STRING,
  SESSION_KEYS,
  routes,
  SALON_SITE_URL,
} from '../../constants';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  getSalonPostList,
  getRecommendPostList,
  createSalonChannel,
} from 'api';
import { EPost, TPostDetail } from 'types';
import PostView from './PostView';
import { usePostStore, useUserStore } from 'store';
import { useNavigate } from 'react-router-dom';
import { PostContext } from 'contexts';
import { useSize, useToast } from 'hooks';
import { trackSalonInquiryBannerClick } from 'utils';
import { VirtuosoHandle } from 'react-virtuoso';

// Upload new images to s3 to change the banner
const [BANNER_URL, RESOLUTION] =
  process.env.REACT_APP_TWEET_BANNER_URL?.split('?resolution=') || [];

const [BANNER_URL_SECONDARY, RESOLUTION_SECONDARY] =
  process.env.REACT_APP_TWEET_BANNER_URL_SECONDARY?.split('?resolution=') || [];
const POSTER_URL = process.env.REACT_APP_POSTER_TWEET_BANNER_URL;
const BANNER_REDIRECT_URL = process.env.REACT_APP_TWEET_BANNER_REDIRECT_URL;
const BANNER_REDIRECT_URL_SECONDARY =
  process.env.REACT_APP_TWEET_BANNER_REDIRECT_URL_SECONDARY;

const tabPost = [
  {
    title: TEXT_STRING.HOME_PAGE.RECOMMEND,
    id: EPost.RECOMMEND,
  },
  {
    title: TEXT_STRING.HOME_PAGE.MEMBER_BOARD,
    id: EPost.MEMBER_BOARD,
  },
];

function readStoredHomeTab(): EPost {
  const raw = sessionStorage.getItem(SESSION_KEYS.HOME_TAB) as EPost;
  if (![EPost.RECOMMEND, EPost.MEMBER_BOARD]?.includes(raw)) {
    sessionStorage.setItem(SESSION_KEYS.HOME_TAB, EPost.RECOMMEND);
    return EPost.RECOMMEND;
  }

  return raw;
}

function Home() {
  // State
  const [tab, setTab] = useState<EPost>(() => readStoredHomeTab());
  const [galleryWidth, setGalleryWidth] = useState<number | undefined>(
    undefined,
  );
  const [pageLoading, setPageLoading] = useState(true);

  // Hook
  const {
    postRecommendVirtuosoState,
    postFollowedVirtuosoState,
    setRecommendVirtuosoState,
    setFollowedVirtuosoState,
  } = usePostStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toastError } = useToast();
  const { isUserApplyABTesting, user } = useUserStore();
  const viewerIsSalonMember = !!user?.isSalonMember;
  const activeTweetBannerUrl = isUserApplyABTesting
    ? BANNER_URL_SECONDARY
    : BANNER_URL;

  const { isLoading: bannerLoading, BannerView } = useSize({
    url: activeTweetBannerUrl,
    resolution: isUserApplyABTesting ? RESOLUTION_SECONDARY : RESOLUTION,
    poster: POSTER_URL,
    className: 'p-4',
    redirectUrl: isUserApplyABTesting
      ? BANNER_REDIRECT_URL_SECONDARY
      : BANNER_REDIRECT_URL,
    autoPlay: true,
  });

  const resolution = isUserApplyABTesting ? RESOLUTION_SECONDARY : RESOLUTION;
  const aspectRatio = resolution ? resolution.replace('x', '/') : '16/9';

  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const [selectedMale, setSelectedMale] = useState<TPostDetail['user'] | null>(
    null,
  );

  const { mutateAsync: mutateCreateSalonChannel } = useMutation({
    mutationFn: (maleId: number) =>
      createSalonChannel({ targetMaleIds: [maleId] }),
  });

  const canStartDmWithSelectedMale = useMemo(
    () => viewerIsSalonMember && !!selectedMale?.isSalonMember,
    [selectedMale?.isSalonMember, viewerIsSalonMember],
  );

  const handleOpenMaleDm = useCallback(async () => {
    if (!selectedMale?.id) return;

    try {
      const channel = await mutateCreateSalonChannel(+selectedMale.id);
      setSelectedMale(null);
      navigate(routes.CONVERSATION.replace(':id', channel.id));
    } catch (error) {
      toastError(new Error(TEXT_STRING.COMMON.SOMETHING_WENT_WRONG));
    }
  }, [mutateCreateSalonChannel, navigate, selectedMale?.id, toastError]);

  const handleOpenSalonInquiryChannel = useCallback(() => {
    trackSalonInquiryBannerClick('tweet', user?.id);
    try {
      window.open(SALON_SITE_URL, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toastError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [toastError, user?.id]);

  // Ref
  const contentRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const recommendVirtuosoState = useRef<VirtuosoHandle>(null);
  const followedVirtuosoState = useRef<VirtuosoHandle>(null);

  // Callbacks, memo
  const handleScrollRecommendPost = useCallback(() => {
    recommendVirtuosoState.current?.getState((state) => {
      const stateToSave = state.scrollTop === 0 ? null : state;
      setRecommendVirtuosoState(stateToSave);
    });
  }, [setRecommendVirtuosoState]);

  const handleScrollFollowedPost = useCallback(() => {
    followedVirtuosoState.current?.getState((state) => {
      const stateToSave = state.scrollTop === 0 ? null : state;
      setFollowedVirtuosoState(stateToSave);
    });
  }, [setFollowedVirtuosoState]);

  const handleChangeTab = useCallback(
    (tab: string) => {
      handleScrollFollowedPost();
      handleScrollRecommendPost();

      const targetTab = tab as EPost;
      let targetHasScroll = false;

      if (
        targetTab === EPost.RECOMMEND &&
        postRecommendVirtuosoState?.scrollTop
      ) {
        targetHasScroll = postRecommendVirtuosoState.scrollTop > 0;
      } else if (
        targetTab === EPost.MEMBER_BOARD &&
        postFollowedVirtuosoState?.scrollTop
      ) {
        targetHasScroll = postFollowedVirtuosoState.scrollTop > 0;
      }

      sessionStorage.setItem(SESSION_KEYS.HOME_TAB, tab);
      setTab(targetTab);

      requestAnimationFrame(() => {
        if (contentRef.current) {
          if (!targetHasScroll) {
            contentRef.current.scrollTop = 0;
          }
        }
      });
    },
    [
      handleScrollFollowedPost,
      handleScrollRecommendPost,
      postRecommendVirtuosoState,
      postFollowedVirtuosoState,
    ],
  );

  const onCreatePost = useCallback(() => {
    navigate(routes.CREATE_POST);
  }, [navigate]);

  const handlePostReady = useCallback(() => {
    setTimeout(() => {
      setPageLoading(false);
    }, 250);
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsPullRefreshing(true);
    const MIN_SPIN_TIME = 500;
    const start = Date.now();

    await queryClient.invalidateQueries({
      queryKey: [
        tab === EPost.RECOMMEND
          ? queryKeys.POST_RECOMMEND
          : queryKeys.POST_MEMBER_BOARD,
      ],
    });

    const elapsed = Date.now() - start;
    if (elapsed < MIN_SPIN_TIME) {
      await new Promise((r) => setTimeout(r, MIN_SPIN_TIME - elapsed));
    }
    setIsPullRefreshing(false);
  }, [tab, queryClient]);

  useEffect(() => {
    if (ref.current?.offsetWidth && galleryWidth !== ref.current?.offsetWidth) {
      setGalleryWidth(ref.current.offsetWidth);
    }
  }, [galleryWidth, setGalleryWidth]);

  return (
    <PostContext.Provider value={{ galleryWidth, setGalleryWidth }}>
      <div ref={contentRef} className="h-full overflow-y-auto w-full relative">
        <PullToRefresh
          scrollElement={contentRef.current}
          isRefreshing={isPullRefreshing}
          onRefresh={handleRefresh}
        >
          <div className="flex flex-col w-full">
            <div className={`flex gap-3 bg-primary h-0 px-4`}>
              <div className={`flex-[1]`}></div>
              <div className={`flex-[7]`} ref={ref}></div>
            </div>
            {pageLoading && (
              <div className="absolute inset-0 z-30 bg-white overflow-hidden min-h-screen">
                <div>
                  <div
                    className="w-full bg-gray-200 animate-pulse rounded-lg"
                    style={{
                      aspectRatio,
                      height: 'auto',
                    }}
                  />
                </div>

                <div className="sticky top-0 z-10 bg-white flex flex-col gap-16px w-full px-3 pt-5">
                  <div className="flex gap-2 justify-center">
                    <div className="h-10 w-24 bg-gray-200 animate-pulse rounded-lg"></div>
                    <div className="h-10 w-24 bg-gray-200 animate-pulse rounded-lg"></div>
                  </div>
                  <hr className="solid" />
                </div>

                {Array.from({ length: 3 }).map((_, i) => (
                  <LoadingPost key={i} index={i} />
                ))}
              </div>
            )}
            {BannerView ? (
              <div style={{ aspectRatio, width: '100%' }}>{BannerView}</div>
            ) : (
              <img
                className="mx-4 mt-4 cursor-pointer"
                onClick={handleOpenSalonInquiryChannel}
                src={SALON_INQUIRY_BANNER_URL}
                alt={TEXT_STRING.SALON_INQUIRY_BANNER.ALT}
              />
            )}
            <div className="sticky top-0 z-10 bg-white flex flex-col gap-16px w-full px-3 pt-5">
              <Tabs
                currentTab={tab}
                tabs={tabPost}
                onChangeTab={handleChangeTab}
              />
              {tab === EPost.MEMBER_BOARD && (
                <Button
                  className="w-full py-4"
                  bold
                  textSize="text-[14px]"
                  onClick={() => navigate(routes.LIST_MALE)}
                >
                  {TEXT_STRING.LIST_MALE.TITLE}
                </Button>
              )}
              <hr className="solid" />
            </div>
            {tab === EPost.RECOMMEND && (
              <PostView
                loading={bannerLoading}
                onClickAvatar={handleScrollRecommendPost}
                queryKey={[queryKeys.POST_RECOMMEND]}
                getPostList={getRecommendPostList}
                enabled={tab === EPost.RECOMMEND}
                virtuosoRef={recommendVirtuosoState}
                virtuosoState={postRecommendVirtuosoState}
                customScrollParent={contentRef.current}
                onReady={handlePostReady}
                onMaleAvatarClick={(maleUser) => setSelectedMale(maleUser)}
              />
            )}
            {tab === EPost.MEMBER_BOARD && (
              <PostView
                loading={bannerLoading}
                onClickAvatar={handleScrollFollowedPost}
                queryKey={[queryKeys.POST_MEMBER_BOARD]}
                getPostList={getSalonPostList}
                enabled={tab === EPost.MEMBER_BOARD}
                virtuosoRef={followedVirtuosoState}
                virtuosoState={postFollowedVirtuosoState}
                customScrollParent={contentRef.current}
                onReady={handlePostReady}
                onMaleAvatarClick={(maleUser) => setSelectedMale(maleUser)}
              />
            )}
            <button
              className="text-white shadow-xl flex items-center justify-center p-4 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 z-10 fixed right-9 bottom-36 scale-125"
              onClick={onCreatePost}
            >
              <Icon icon="plus" />
            </button>
            <Modal
              open={!!selectedMale}
              onClose={() => setSelectedMale(null)}
              className="bg-white px-3 py-4"
              w="w-[calc(100%-12px)]"
            >
              <div className="flex flex-col gap-4">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setSelectedMale(null)}
                    className="cursor-pointer"
                  >
                    <Icon icon="close" />
                  </button>
                </div>
                {selectedMale && (
                  <>
                    <MaleCard
                      name={selectedMale.nickname}
                      avatar={selectedMale.avatar}
                      biography={selectedMale.biography}
                      ranking={selectedMale.ranking}
                      isSalonMember={!!selectedMale.isSalonMember}
                      isSalonPremiumMember={!!selectedMale.isSalonPremiumMember}
                    />
                    {canStartDmWithSelectedMale && (
                      <Button
                        className="w-full p-5"
                        bold
                        textSize="text-[14px]"
                        onClick={handleOpenMaleDm}
                      >
                        {TEXT_STRING.LIST_MALE.START_DM}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Modal>
          </div>
        </PullToRefresh>
      </div>
    </PostContext.Provider>
  );
}

export default Home;
